import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, Modal, Form, Alert, Badge, Row, Col } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import AttachmentList from '../components/AttachmentList.jsx';
import { fmtMoney, fmtDate, PAYMENT_METHOD_LABEL } from '../lib/format.js';

const EMPTY = {
  expense_date: new Date().toISOString().slice(0, 10),
  vendor: '',
  vendor_invoice_number: '',
  description: '',
  category_id: '',
  net_amount: '',
  vat_rate: 19,
  paid: false,
  paid_at: '',
  payment_method: 'bank_transfer',
  note: ''
};

export default function ExpensesPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const canWrite = auth.can('expenses', 'create');
  const canDelete = auth.can('expenses', 'delete');

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [paidFilter, setPaidFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const expenses = useQuery({
    queryKey: ['expenses', year, paidFilter, categoryFilter],
    queryFn: () => api.get('/expenses', { params: {
      limit: 1000, year, paid: paidFilter || undefined, category_id: categoryFilter || undefined
    } }).then(r => r.data.items)
  });
  const summary = useQuery({
    queryKey: ['expense-summary', year],
    queryFn: () => api.get('/expenses/summary', { params: { year } }).then(r => r.data.summary)
  });
  const categories = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () => api.get('/expenses/categories').then(r => r.data.items)
  });

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [error, setError] = useState(null);

  const detail = useQuery({
    queryKey: ['expense', editingId],
    queryFn: () => api.get(`/expenses/${editingId}`).then(r => r.data),
    enabled: !!editingId && showModal
  });

  const [autofillHint, setAutofillHint] = useState(null);

  // After an attachment is uploaded to a NEW (empty) expense, try to extract
  // fields from it (PDF text → vendor / amount / date / VAT) and pre-fill.
  const tryAutofill = async (attachments) => {
    if (!attachments?.length) return;
    const looksEmpty = !draft.vendor && !draft.description && (!draft.net_amount || draft.net_amount === '');
    if (!looksEmpty) return;
    const a = attachments[0];
    if (a.mime_type !== 'application/pdf') return;
    try {
      const { data } = await api.post(`/attachments/${a.id}/extract`);
      if (!data.extracted) return;
      const f = data.fields || {};
      setDraft(d => ({
        ...d,
        vendor:                d.vendor                || f.vendor                  || '',
        description:           d.description           || (f.invoice_number ? `Beleg ${f.invoice_number}` : ''),
        vendor_invoice_number: d.vendor_invoice_number || f.invoice_number          || '',
        expense_date:          (d.expense_date && d.expense_date !== EMPTY.expense_date) ? d.expense_date : (f.date || d.expense_date),
        net_amount:            d.net_amount            || (f.amount_net_cents != null ? (f.amount_net_cents / 100).toFixed(2) : ''),
        vat_rate:              f.vat_rate ?? d.vat_rate
      }));
      const hitCount = Object.keys(f.hits || {}).length;
      setAutofillHint(hitCount > 0
        ? `Felder aus PDF übernommen (${hitCount} Treffer) — bitte prüfen.`
        : null);
    } catch { /* silent — extraction is best-effort */ }
  };

  const open = (exp) => {
    if (exp) {
      setEditingId(exp.id);
      setDraft({
        expense_date: exp.expense_date || EMPTY.expense_date,
        vendor: exp.vendor,
        vendor_invoice_number: exp.vendor_invoice_number || '',
        description: exp.description,
        category_id: exp.category_id || '',
        net_amount: (exp.net_cents / 100).toString(),
        vat_rate: exp.vat_rate,
        paid: exp.paid,
        paid_at: exp.paid_at ? exp.paid_at.slice(0, 10) : '',
        payment_method: exp.payment_method || 'bank_transfer',
        note: exp.note || ''
      });
    } else {
      setEditingId(null);
      setDraft(EMPTY);
    }
    setError(null);
    setShowModal(true);
  };
  const close = () => { setShowModal(false); setEditingId(null); };

  const save = useMutation({
    mutationFn: () => {
      const body = {
        expense_date: draft.expense_date,
        vendor: draft.vendor,
        vendor_invoice_number: draft.vendor_invoice_number || null,
        description: draft.description,
        category_id: draft.category_id || null,
        vat_rate: parseFloat(draft.vat_rate) || 0,
        net_cents: Math.round(parseFloat(draft.net_amount || '0') * 100),
        paid: draft.paid,
        paid_at: draft.paid && draft.paid_at ? new Date(draft.paid_at).toISOString() : null,
        payment_method: draft.paid ? draft.payment_method : null,
        note: draft.note || null
      };
      return editingId
        ? api.patch(`/expenses/${editingId}`, body).then(r => ({ id: editingId, ...r.data }))
        : api.post('/expenses', body).then(r => ({ id: r.data.expense.id, ...r.data }));
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['expenses'] });
      qc.invalidateQueries({ queryKey: ['expense-summary'] });
      // After create, switch into edit mode so the user can add attachments to the same record.
      if (!editingId) {
        setEditingId(r.id);
      }
    },
    onError: (e) => setError(e.response?.data?.message
      || (e.response?.data?.details && JSON.stringify(e.response.data.details))
      || e.message)
  });

  const togglePaid = useMutation({
    mutationFn: ({ id, paid }) => api.post(`/expenses/${id}/paid`, { paid }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['expenses'] }); qc.invalidateQueries({ queryKey: ['expense-summary'] }); }
  });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/expenses/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['expenses'] }); qc.invalidateQueries({ queryKey: ['expense-summary'] }); close(); }
  });

  const exportCsv = async () => {
    try {
      const params = { year };
      if (paidFilter) params.paid = paidFilter;
      if (categoryFilter) params.category_id = categoryFilter;
      const r = await api.get('/expenses/export.csv', { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ausgaben-${year === 'all' ? 'gesamt' : year}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const set = (k) => (e) => setDraft(d => ({ ...d, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const sum = summary.data || { gross_total: 0, gross_paid: 0, gross_open: 0, count: 0 };
  const years = [];
  for (let y = currentYear; y >= currentYear - 5; y--) years.push(y);

  return (
    <>
      <PageHeader title="Ausgaben" subtitle="Lieferantenrechnungen, Belege, Quittungen — alles auf einem Fleck"
        actions={
          <>
            {canWrite && (
              <Button as={Link} to="/expenses/import" variant="outline-primary"
                title="Mehrere Belege auf einmal hochladen, OCR liest die Felder aus">
                <Icon icon="tabler:cloud-upload" className="me-1" />Belege importieren
              </Button>
            )}
            <Button variant="outline-secondary" onClick={() => exportCsv()}>
              <Icon icon="tabler:download" className="me-1" />CSV-Export
            </Button>
            {canWrite && <Button onClick={() => open(null)}><Icon icon="tabler:plus" className="me-1" />Neue Ausgabe</Button>}
          </>
        } />

      <Row className="g-3 mb-3">
        <Col md={3}><Card className="card-flat"><Card.Body>
          <div className="text-muted small text-uppercase">Gesamt {year === 'all' ? '(alle Jahre)' : year}</div>
          <div className="fs-4 fw-semibold">{fmtMoney(sum.gross_total)}</div>
          <div className="text-muted small">{sum.count} Buchungen</div>
        </Card.Body></Card></Col>
        <Col md={3}><Card className="card-flat"><Card.Body>
          <div className="text-muted small text-uppercase">Bezahlt</div>
          <div className="fs-4 fw-semibold text-success">{fmtMoney(sum.gross_paid)}</div>
        </Card.Body></Card></Col>
        <Col md={3}><Card className="card-flat"><Card.Body>
          <div className="text-muted small text-uppercase">Offen</div>
          <div className={`fs-4 fw-semibold ${sum.gross_open > 0 ? 'text-warning' : ''}`}>{fmtMoney(sum.gross_open)}</div>
        </Card.Body></Card></Col>
      </Row>

      <div className="d-flex gap-2 flex-wrap mb-3">
        <Form.Select size="sm" style={{ width: 120 }} value={year}
          onChange={(e) => setYear(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}>
          <option value="all">Gesamt</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </Form.Select>
        <Form.Select size="sm" style={{ width: 200 }} value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="">Alle Kategorien</option>
          {(categories.data || []).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </Form.Select>
        <Form.Select size="sm" style={{ width: 160 }} value={paidFilter} onChange={(e) => setPaidFilter(e.target.value)}>
          <option value="">Alle</option>
          <option value="false">Offen</option>
          <option value="true">Bezahlt</option>
        </Form.Select>
      </div>

      <Card className="card-flat">
        <Card.Body className="p-0">
          {expenses.isLoading && <div className="p-4 text-muted">Lade…</div>}
          {expenses.data && expenses.data.length === 0 && (
            <div className="empty-state">
              <Icon icon="tabler:receipt-off" className="empty-icon" />
              <div>Keine Ausgaben {year === 'all' ? 'erfasst' : `in ${year}`}.</div>
            </div>
          )}
          {expenses.data && expenses.data.length > 0 && (
            <Table hover className="mb-0 align-middle">
              <thead><tr>
                <th>Nr.</th><th>Datum</th><th>Lieferant / Beschreibung</th><th>Kategorie</th>
                <th className="text-end">Brutto</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {expenses.data.map(e => (
                  <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => open(e)}>
                    <td><code className="small">{e.expense_number}</code></td>
                    <td className="small">{fmtDate(e.expense_date)}</td>
                    <td>
                      <div className="fw-semibold">{e.vendor}</div>
                      <div className="text-muted small">{e.description}</div>
                    </td>
                    <td>{e.category_label
                      ? <Badge bg="" className="badge-soft-secondary">{e.category_label}</Badge>
                      : <span className="text-muted small">—</span>}</td>
                    <td className="text-end">{fmtMoney(e.gross_cents, e.currency)}</td>
                    <td>{e.paid
                      ? <Badge bg="" className="badge-soft-success">Bezahlt</Badge>
                      : <Badge bg="" className="badge-soft-warning">Offen</Badge>}</td>
                    <td className="text-end pe-3" onClick={(ev) => ev.stopPropagation()}>
                      <Button size="sm" variant="outline-secondary" className="me-1" title="Anzeigen / Bearbeiten" onClick={() => open(e)}>
                        <Icon icon="tabler:eye" />
                      </Button>
                      <Button size="sm" variant="outline-secondary" className="me-1" title={e.paid ? 'Als unbezahlt markieren' : 'Als bezahlt markieren'}
                        onClick={() => togglePaid.mutate({ id: e.id, paid: !e.paid })}>
                        <Icon icon={e.paid ? 'tabler:circle-x' : 'tabler:check'} />
                      </Button>
                      {canDelete && (
                        <Button size="sm" variant="outline-danger" title="Löschen"
                          onClick={() => confirm('Ausgabe und Anhänge wirklich löschen?') && remove.mutate(e.id)}>
                          <Icon icon="tabler:trash" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={close} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId
              ? <>Ausgabe bearbeiten {detail.data?.expense?.expense_number && <code className="ms-2 small">{detail.data.expense.expense_number}</code>}</>
              : 'Neue Ausgabe'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Belegdatum *</Form.Label>
                <Form.Control type="date" value={draft.expense_date} onChange={set('expense_date')} required />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Kategorie</Form.Label>
                <Form.Select value={draft.category_id} onChange={set('category_id')}>
                  <option value="">— wählen —</option>
                  {(categories.data || []).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Belegnummer Lieferant</Form.Label>
                <Form.Control value={draft.vendor_invoice_number} onChange={set('vendor_invoice_number')}
                  placeholder="z.B. RG-2026-0042" />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Lieferant *</Form.Label>
                <Form.Control value={draft.vendor} onChange={set('vendor')} required placeholder="z.B. Hetzner Online GmbH" />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Beschreibung *</Form.Label>
                <Form.Control as="textarea" rows={2} value={draft.description} onChange={set('description')} required />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Netto-Betrag (€)</Form.Label>
                <Form.Control type="number" step="0.01" min="0" value={draft.net_amount} onChange={set('net_amount')} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>MwSt.-Satz (%)</Form.Label>
                <Form.Control type="number" step="0.5" min="0" max="100" value={draft.vat_rate} onChange={set('vat_rate')} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Brutto (errechnet)</Form.Label>
                <Form.Control disabled value={(() => {
                  const n = parseFloat(draft.net_amount || '0');
                  const v = parseFloat(draft.vat_rate || '0');
                  return ((n + n * v / 100) || 0).toFixed(2) + ' €';
                })()} />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Check type="switch" id="paid-switch" label="Bereits bezahlt"
                checked={draft.paid} onChange={set('paid')} />
            </Col>
            {draft.paid && <>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Zahlungsdatum</Form.Label>
                  <Form.Control type="date" value={draft.paid_at} onChange={set('paid_at')} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Zahlungsart</Form.Label>
                  <Form.Select value={draft.payment_method} onChange={set('payment_method')}>
                    {Object.entries(PAYMENT_METHOD_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
            </>}

            <Col md={12}>
              <Form.Group>
                <Form.Label>Notiz</Form.Label>
                <Form.Control as="textarea" rows={2} value={draft.note} onChange={set('note')} />
              </Form.Group>
            </Col>
          </Row>

          {/* Anhänge — nur sichtbar nachdem die Ausgabe gespeichert ist */}
          {editingId && (
            <>
              <hr className="my-4" />
              <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>
                Belege / Anhänge
              </h6>
              {autofillHint && (
                <Alert variant="info" className="py-2 small">
                  <Icon icon="tabler:wand" className="me-1" />{autofillHint}
                </Alert>
              )}
              <AttachmentList parentType="expense" parentId={editingId}
                items={detail.data?.attachments || []}
                onChange={async () => {
                  const r = await qc.invalidateQueries({ queryKey: ['expense', editingId] });
                  // After invalidation, the query refetches; trigger autofill on the next attachments list.
                  const fresh = await api.get(`/expenses/${editingId}`).then(x => x.data);
                  await tryAutofill(fresh.attachments);
                  qc.setQueryData(['expense', editingId], fresh);
                }} />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {editingId && canDelete && (
            <Button variant="outline-danger" className="me-auto"
              onClick={() => confirm('Ausgabe und Anhänge wirklich löschen?') && remove.mutate(editingId)}>
              <Icon icon="tabler:trash" className="me-1" />Löschen
            </Button>
          )}
          <Button variant="outline-secondary" onClick={close}>Schließen</Button>
          <Button onClick={() => save.mutate()}
            disabled={!draft.vendor || !draft.description || save.isLoading}>
            {save.isLoading ? 'Speichere…' : editingId ? 'Speichern' : 'Anlegen + Belege hinzufügen'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
